'use client';

import { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { BookingPoweredBy } from '../booking/BookingPoweredBy';
import { BookingFormSkeleton } from '../booking/BookingFormSkeleton';
import type { EventData } from '@/lib/fetch-event-data';

const BookingWidget = dynamic(
  () => import('../booking/BookingWidget').then((m) => ({ default: m.BookingWidget })),
  { ssr: false, loading: () => <BookingFormSkeleton variant="smartflow" fullHeight /> },
);

interface Props {
  eventData: EventData;
  open: boolean;
  onClose: () => void;
}

/** Polished native booking popup for SmartFlow forms on salespads. Recreates the
 * external embed's layout (logo top, form centred, full-width footer bottom,
 * desktop-centred / mobile full-screen). The box carries `transform: translateZ(0)`
 * so the widget's `position: fixed` progress bar pins to the box, not the page. */
export function BookingModalPopup({ eventData, open, onClose }: Props) {
  useEffect(() => {
    if (!open) return;
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const ev = eventData.event;
  const showLogo = ev.show_logo ?? true;
  const logoUrl = ev.custom_logo_url || eventData.profile.company_logo_url || null;

  return (
    <div
      className="fixed inset-0 z-[2147483646] flex items-center justify-center bg-slate-900/55 p-8 max-[719px]:p-0 [@media(max-height:600px)]:p-0"
      onClick={onClose}
    >
      <div
        className="relative flex h-[min(720px,100%)] max-h-full w-[min(960px,100%)] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl max-[719px]:h-screen max-[719px]:w-screen max-[719px]:rounded-none [@media(max-height:600px)]:h-screen [@media(max-height:600px)]:w-screen [@media(max-height:600px)]:rounded-none"
        style={{ transform: 'translateZ(0)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 z-[2] flex h-9 w-9 items-center justify-center rounded-full text-slate-600 transition-colors hover:bg-black/[0.06]"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>

        {showLogo && logoUrl && (
          <div className="flex shrink-0 justify-center px-4 pb-4 pt-8">
            <img src={logoUrl} alt={eventData.profile.company_name || 'Company logo'} className="h-9 max-w-[200px] object-contain" />
          </div>
        )}

        <div className="flex min-h-0 flex-1 flex-col justify-center overflow-y-auto px-4 py-4">
          <BookingWidget
            event={eventData.event}
            fields={eventData.fields}
            ruleSets={eventData.ruleSets}
            profile={eventData.profile}
            userEmail={eventData.profile.email}
            metaPixelIds={eventData.metaPixelIds}
            teamId={eventData.teamId}
            metaPixelEventNames={eventData.metaPixelEventNames}
            confirmationIncludeCancelReschedule={eventData.confirmationIncludeCancelReschedule}
            gtmContainerId={eventData.gtmContainerId}
            eventSlug={eventData.event.slug}
            bookingPaused={eventData.bookingPaused}
            hideLogo
            hidePoweredBy
          />
        </div>

        <div className="shrink-0">
          <BookingPoweredBy />
        </div>
      </div>
    </div>
  );
}
